import { type FC, useCallback } from 'react';

import { BackgroundVideo } from 'shared/components/BackgroundVideo';
import { ButtonLink } from 'shared/components/Button';
import { Layout } from 'shared/components/Layout';
import { Marquee } from 'shared/components/Marquee';
import { type MainCarouselBanner } from 'shared/types/mainCarousel';

import styles from './CarouselMainV2Slide.css';

const CAROUSEL_IMAGE_HEIGHT = 780;
const CAROUSEL_IMAGE_WIDTH = 1920;

type CarouselMainV2SlideProps = {
  active: boolean;
  banner: MainCarouselBanner;
  index: number;
  testId: string;
  onSelectApartmentClick: (newbuildingId: number, index: number) => void;
  onTickerMouseLeave: () => void;
  onTickerMouseOver: () => void;
};

export const CarouselMainV2Slide: FC<CarouselMainV2SlideProps> = props => {
  const { active, banner, index, testId, onSelectApartmentClick, onTickerMouseLeave, onTickerMouseOver } = props;
  const mediaLoading = index === 0 ? 'eager' : 'lazy';
  const fetchPriority = index === 0 && active ? 'high' : 'low';

  const handleSelectApartmentClick = useCallback((): void => {
    onSelectApartmentClick(banner.id, index);
  }, [banner.id, index, onSelectApartmentClick]);

  return (
    <article className={styles['slide']} data-testid={testId}>
      <div className={styles['media']}>
        {banner.video ? (
          <BackgroundVideo
            active={active}
            fetchPriority={fetchPriority}
            loading={mediaLoading}
            poster={banner.video.posterUrl}
            url={banner.video.url}
          />
        ) : (
          <img
            alt={banner.title}
            className={styles['image']}
            data-testid="main-carousel-v2-image"
            decoding={mediaLoading === 'eager' ? 'sync' : 'async'}
            fetchPriority={fetchPriority}
            height={CAROUSEL_IMAGE_HEIGHT}
            loading={mediaLoading}
            src={banner.imageUrl}
            width={CAROUSEL_IMAGE_WIDTH}
          />
        )}
      </div>
      <div className={styles['content']}>
        <Layout stretch>
          <div className={styles['content-inner']}>
            <div className={styles['main-content']}>
              <h1 className={styles['title']} data-testid="main-carousel-v2-title">
                {banner.title}
              </h1>
              {banner.label && (
                <p className={styles['subtitle']} data-testid="main-carousel-v2-label">
                  {banner.label}
                </p>
              )}
            </div>

            <div className={styles['button-wrapper']}>
              <ButtonLink href={banner.publicUrl} testId="select-apartment-button" onClick={handleSelectApartmentClick}>
                Выбрать квартиру
              </ButtonLink>
            </div>

            {banner.disclaimer && (
              <div className={styles['disclaimer']} data-testid="main-carousel-v2-disclaimer">
                <Marquee text={banner.disclaimer} onMouseLeave={onTickerMouseLeave} onMouseOver={onTickerMouseOver} />
              </div>
            )}
          </div>
        </Layout>
      </div>
    </article>
  );
};
